import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../Config/config";
import CollegeHeader from "./CollegeHeader";
import CollegeHomeSideBar from "./CollegeHomeSideBar";
import { MessageCircle, CheckCircle, AlertCircle, Clock, Send, Bug } from "lucide-react";

// --- Design Tokens ------------------------------------------------------------

const T = {
  border:  "1px solid var(--border-subtle, #E2E8F0)",
  radius:  { sm: "8px", md: "12px", lg: "16px", xl: "20px", full: "999px" },
  shadow:  {
    xs: "0 1px 3px rgba(0,0,0,0.05)",
    sm: "0 2px 8px rgba(0,0,0,0.07)",
    md: "0 4px 20px rgba(0,0,0,0.08)",
  },
  brand:   "var(--brand-primary, var(--primary-blue, #4F46E5))",
  text: {
    heading:  "var(--text-heading,  #0F172A)",
    body:     "var(--text-body,     #334155)",
    muted:    "var(--text-muted,    #64748B)",
    disabled: "var(--text-disabled, #94A3B8)",
  },
  bg: {
    main:    "var(--bg-main,    #F8FAFC)",
    section: "var(--bg-section, #FFFFFF)",
  },
};

// --- Support categories (UI-only display) ------------------------------------

const CATEGORIES = [
  { value: "Bug",      label: "Report a Bug",    icon: <Bug size={14} />, desc: "Unexpected errors, broken features, or incorrect behaviour" },
  { value: "Feedback", label: "Feedback",         icon: <MessageCircle size={14} />, desc: "Suggestions, feature requests, or general platform feedback" },
  { value: "Billing",  label: "Billing",          icon: <AlertCircle size={14} />, desc: "Credit purchase issues, invoice queries, or payment disputes" },
  { value: "Other",    label: "Other Query",      icon: <Send size={14} />, desc: "Anything else that doesn't fit the categories above" },
];

const RESPONSE_TIMES = [
  { icon: <Bug size={14} />, label: "Bug Reports",     time: "Within 24 hrs" },
  { icon: <AlertCircle size={14} />, label: "Billing Issues",  time: "Within 12 hrs" },
  { icon: <MessageCircle size={14} />, label: "Feedback",        time: "Within 48 hrs" },
  { icon: <Send size={14} />, label: "General Queries", time: "Within 48 hrs" },
];

// --- Shared input style -------------------------------------------------------

const inputBase = {
  width: "100%",
  padding: "10px 14px",
  fontSize: "13px",
  fontWeight: 500,
  background: T.bg.main,
  border: T.border,
  borderRadius: T.radius.md,
  color: T.text.body,
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block",
  fontSize: "11px",
  fontWeight: 700,
  color: T.text.muted,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  marginBottom: "7px",
};

// --- SupportCollege -----------------------------------------------------------

const SupportCollege = () => {
  const [email,           setEmail]           = useState("");
  const [userId,          setUserId]          = useState(null);
  const [message,         setMessage]         = useState("");
  const [category,        setCategory]        = useState("Bug");
  const [success,         setSuccess]         = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [submitting,      setSubmitting]      = useState(false);
  const [focusedField,    setFocusedField]    = useState(null);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser?.id && storedUser?.email) {
      setUserId(storedUser.id);
      setEmail(storedUser.email);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !message || !category) {
      alert("Please fill in all fields.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/support`, {
        user_email: email,
        category:   category,
        message:    message,
        user_type:  "college",
      });

      if (res.status === 201 && res.data.message) {
        setSuccess(true);
        setResponseMessage(res.data.message);
        setMessage("");
        setCategory("Bug");
      } else {
        alert("Unexpected response from server.");
      }
    } catch (err) {
      console.error("Error submitting ticket:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const focusStyle = (field) =>
    focusedField === field
      ? { borderColor: "var(--brand-primary, var(--primary-blue, #4F46E5))", boxShadow: "0 0 0 3px rgba(79, 70, 229,0.10)" }
      : {};

  return (
    <div className="college-main-layout" style={{ background: T.bg.main, fontFamily: "inherit" }}>
      <CollegeHeader />

      <div className="college-content-wrapper">
        <CollegeHomeSideBar />

        <main className="college-main-content">
          <div style={{ maxWidth: "1060px", margin: "0 auto", padding: "36px 28px 72px" }}>

            {/* -- Page Header ---------------------------------------------- */}
            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "32px" }}>
              <div style={{
                width: "48px", height: "48px", borderRadius: T.radius.lg,
                background: "rgba(79, 70, 229,0.08)", border: "1px solid rgba(79, 70, 229,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <MessageCircle style={{ width: "22px", height: "22px", color: T.brand }} />
              </div>
              <div>
                <h1 style={{
                  fontSize: "22px", fontWeight: 800, margin: 0,
                  color: T.text.heading, letterSpacing: "-0.02em", lineHeight: 1.2,
                }}>
                  Support Center
                </h1>
                <p style={{
                  fontSize: "13px", color: T.text.muted,
                  margin: "4px 0 0", fontWeight: 500,
                }}>
                  Submit a ticket and our team will get back to you shortly.
                </p>
              </div>
            </div>

            {/* -- Two-column layout --------------------------------------- */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1.6fr",
              gap: "24px",
              alignItems: "start",
            }}>

              {/* -- LEFT PANEL: Info --------------------------------------- */}
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

                {/* What can we help with */}
                <div style={{
                  background: "#FFFFFF", border: T.border,
                  borderRadius: T.radius.xl, overflow: "hidden",
                  boxShadow: T.shadow.sm,
                }}>
                  <div style={{ padding: "22px" }}>
                    <p style={{
                      fontSize: "11px", fontWeight: 700, color: T.text.muted,
                      textTransform: "uppercase", letterSpacing: "0.06em",
                      margin: "0 0 14px",
                    }}>
                      What can we help with?
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {CATEGORIES.map((cat) => (
                        <div
                          key={cat.value}
                          onClick={() => { setCategory(cat.value); setSuccess(false); }}
                          style={{
                            display: "flex", alignItems: "flex-start", gap: "12px",
                            padding: "12px 14px", borderRadius: T.radius.md,
                            border: category === cat.value
                              ? "1.5px solid rgba(79, 70, 229,0.35)"
                              : T.border,
                            background: category === cat.value
                              ? "rgba(79, 70, 229,0.04)"
                              : T.bg.main,
                            cursor: "pointer",
                            transition: "all 0.15s",
                          }}
                        >
                          <span style={{ fontSize: "16px", flexShrink: 0, marginTop: "1px" }}>
                            {cat.icon}
                          </span>
                          <div>
                            <div style={{
                              fontSize: "13px", fontWeight: 700,
                              color: category === cat.value ? T.brand : T.text.heading,
                              marginBottom: "2px",
                            }}>
                              {cat.label}
                            </div>
                            <div style={{ fontSize: "11px", color: T.text.muted, lineHeight: 1.5 }}>
                              {cat.desc}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Response time card */}
                <div style={{
                  background: "#FFFFFF", border: T.border,
                  borderRadius: T.radius.xl, padding: "20px 22px",
                  boxShadow: T.shadow.xs,
                }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: "8px",
                    marginBottom: "14px",
                  }}>
                    <Clock style={{ width: "14px", height: "14px", color: T.brand }} />
                    <p style={{
                      fontSize: "11px", fontWeight: 700, color: T.text.muted,
                      textTransform: "uppercase", letterSpacing: "0.06em", margin: 0,
                    }}>
                      Expected Response Times
                    </p>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "9px" }}>
                    {RESPONSE_TIMES.map((item) => (
                      <div key={item.label} style={{
                        display: "flex", alignItems: "center",
                        justifyContent: "space-between",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: T.text.body, fontWeight: 500 }}>
                          {item.icon} {item.label}
                        </div>
                        <span style={{
                          fontSize: "11px", fontWeight: 700,
                          color: T.brand,
                          background: "rgba(79, 70, 229,0.07)",
                          padding: "2px 10px", borderRadius: T.radius.full,
                        }}>
                          {item.time}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick tip */}
                <div style={{
                  background: "rgba(79, 70, 229,0.04)",
                  border: "1px solid rgba(79, 70, 229,0.14)",
                  borderRadius: T.radius.lg, padding: "16px 18px",
                }}>
                  <p style={{
                    fontSize: "12px", fontWeight: 700, color: T.brand,
                    margin: "0 0 6px", letterSpacing: "0.02em",
                    display: "flex", alignItems: "center", gap: "6px"
                  }}>
                    <Clock size={14} /> For faster resolution
                  </p>
                  <p style={{
                    fontSize: "12px", color: T.text.body,
                    lineHeight: 1.65, margin: 0, fontWeight: 500,
                  }}>
                    Include the specific page or feature name, steps to reproduce (for bugs),
                    and any error messages you saw. This helps our team resolve your issue faster.
                  </p>
                </div>
              </div>

              {/* -- RIGHT PANEL: Form --------------------------------------- */}
              <div style={{
                background: "#FFFFFF", border: T.border,
                borderRadius: T.radius.xl, overflow: "hidden",
                boxShadow: T.shadow.sm,
              }}>
                <div style={{ padding: "28px 32px 32px" }}>
                  <p style={{
                    fontSize: "16px", fontWeight: 800, color: T.text.heading,
                    margin: "0 0 4px", letterSpacing: "-0.01em",
                  }}>
                    Submit a Support Ticket
                  </p>
                  <p style={{
                    fontSize: "13px", color: T.text.muted,
                    margin: "0 0 28px", fontWeight: 500,
                  }}>
                    Our support team will respond to the registered email address.
                  </p>

                  {/* Success banner */}
                  {success && (
                    <div style={{
                      display: "flex", alignItems: "flex-start", gap: "12px",
                      padding: "14px 18px", borderRadius: T.radius.lg,
                      background: "#F0FDF4", border: "1.5px solid #BBF7D0",
                      marginBottom: "24px",
                    }}>
                      <CheckCircle style={{ width: "18px", height: "18px", color: "#16A34A", flexShrink: 0, marginTop: "1px" }} />
                      <div>
                        <p style={{ fontSize: "13px", fontWeight: 700, color: "#14532D", margin: "0 0 2px" }}>
                          Ticket Submitted Successfully
                        </p>
                        <p style={{ fontSize: "12px", color: "#15803D", margin: 0, fontWeight: 500 }}>
                          {responseMessage || "We've received your request and will respond shortly."}
                        </p>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

                    {/* Email */}
                    <div>
                      <label style={labelStyle}>Email Address</label>
                      <input
                        type="email"
                        value={email}
                        disabled
                        style={{
                          ...inputBase,
                          background: "#F8FAFC",
                          color: T.text.muted,
                          cursor: "not-allowed",
                        }}
                      />
                      <p style={{
                        fontSize: "11px", color: T.text.disabled,
                        margin: "5px 0 0", fontWeight: 500,
                      }}>
                        Auto-filled from your account · replies sent here
                      </p>
                    </div>

                    {/* Category */}
                    <div>
                      <label style={labelStyle}>Issue Category</label>
                      <div style={{ position: "relative" }}>
                        <select
                          value={category}
                          onChange={(e) => { setCategory(e.target.value); setSuccess(false); }}
                          onFocus={() => setFocusedField("category")}
                          onBlur={() => setFocusedField(null)}
                          required
                          style={{
                            ...inputBase,
                            paddingRight: "32px",
                            appearance: "none",
                            cursor: "pointer",
                            ...focusStyle("category"),
                          }}
                        >
                          {CATEGORIES.map((cat) => (
                            <option key={cat.value} value={cat.value}>
                              {cat.label}
                            </option>
                          ))}
                        </select>
                        <div style={{
                          position: "absolute", right: "12px", top: "50%",
                          transform: "translateY(-50%)", pointerEvents: "none",
                          fontSize: "11px", color: T.text.muted,
                          display: "flex", alignItems: "center"
                        }}>
                          <Clock size={12} />
                        </div>
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label style={labelStyle}>
                        Describe Your Issue
                        <span style={{
                          fontSize: "10px", fontWeight: 500, color: T.text.disabled,
                          textTransform: "none", letterSpacing: 0, marginLeft: "8px",
                        }}>
                          {message.length} characters
                        </span>
                      </label>
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onFocus={() => setFocusedField("message")}
                        onBlur={() => setFocusedField(null)}
                        required
                        rows={6}
                        placeholder="Describe your issue in detail — include the page name, steps to reproduce, and any error messages you saw..."
                        style={{
                          ...inputBase,
                          resize: "vertical",
                          lineHeight: 1.65,
                          minHeight: "140px",
                          ...focusStyle("message"),
                        }}
                      />
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={submitting || !message.trim()}
                      style={{
                        width: "100%",
                        padding: "13px 0",
                        borderRadius: T.radius.md,
                        border: "none",
                        background: submitting || !message.trim()
                          ? "#94A3B8"
                          : "linear-gradient(135deg, var(--primary-blue, #4F46E5), #4F46E5)",
                        color: "#FFFFFF",
                        fontSize: "14px",
                        fontWeight: 700,
                        cursor: submitting || !message.trim() ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        boxShadow: submitting || !message.trim()
                          ? "none"
                          : "0 4px 14px rgba(79, 70, 229,0.30)",
                        transition: "opacity 0.15s, transform 0.1s",
                        letterSpacing: "0.01em",
                      }}
                      onMouseEnter={(e) => {
                        if (!submitting && message.trim()) e.currentTarget.style.opacity = "0.9";
                      }}
                      onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                    >
                      {submitting ? (
                        <>
                          <span style={{ fontSize: "12px", animation: "spin 1s linear infinite" }}>↻</span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send style={{ width: "15px", height: "15px" }} />
                          Submit Ticket
                        </>
                      )}
                    </button>

                    <p style={{
                      fontSize: "11px", color: T.text.disabled,
                      textAlign: "center", margin: 0, fontWeight: 500,
                    }}>
                      By submitting, you agree to our support terms. Tickets are reviewed
                      by our team during business hours (Mon-Sat, 9am-6pm IST).
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default SupportCollege;


